import React, { useEffect, useState } from "react";
import { axiosInstance } from "../../../Api/config";
import { MdOutlinePhotoSizeSelectActual, MdAdd, MdEdit, MdDelete, MdCheck, MdClose } from "react-icons/md";

const HomeSlides = () => {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingSlide, setEditingSlide] = useState(null);

  // Form fields
  const [form, setForm] = useState({
    label: "",
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const fetchSlides = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/home-slides");
      setSlides(response.data || []);
    } catch (err) {
      console.error("Error fetching home slides:", err);
      setError("Failed to fetch homepage slides.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSlides();
  }, []);

  const handleOpenAdd = () => {
    setEditingSlide(null);
    setForm({ label: "" });
    setImageFile(null);
    setImagePreview("");
    setError("");
    setModalOpen(true);
  };

  const handleOpenEdit = (slide) => {
    setEditingSlide(slide);
    setForm({ label: slide.label || "" });
    setImageFile(null);
    setImagePreview(slide.image ? `${axiosInstance.defaults.baseURL}/${slide.image}` : "");
    setError("");
    setModalOpen(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.label) {
      setError("Overlay label is required.");
      return;
    }

    if (!editingSlide && !imageFile) {
      setError("Slider image is required.");
      return;
    }

    try {
      const data = new FormData();
      data.append("label", form.label);
      if (imageFile) {
        data.append("image", imageFile);
      }

      let res;
      if (editingSlide) {
        res = await axiosInstance.post(`/home-slides/${editingSlide.id}`, data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        res = await axiosInstance.post("/home-slides", data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      if (res.data.success) {
        setSuccessMsg(editingSlide ? "Slider image updated!" : "Slider image added!");
        setModalOpen(false);
        fetchSlides();
        setTimeout(() => setSuccessMsg(""), 3000);
      } else {
        setError(res.data.message || "Operation failed.");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to save slider details.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this slide?")) return;
    try {
      const res = await axiosInstance.delete(`/home-slides/${id}`);
      if (res.data.success) {
        setSuccessMsg("Slider image deleted!");
        fetchSlides();
        setTimeout(() => setSuccessMsg(""), 3000);
      }
    } catch (err) {
      console.error(err);
      setError("Failed to delete slide.");
    }
  };

  const handleToggleActive = async (id) => {
    try {
      const res = await axiosInstance.post(`/home-slides/toggle/${id}`);
      if (res.data.success) {
        fetchSlides();
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-amber-600">Admin</p>
          <h1 className="mt-2 text-3xl font-bold text-slate-950">Homepage Slider</h1>
        </div>
        <button
          onClick={handleOpenAdd}
          className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-slate-950 px-4 py-2.5 rounded-lg text-sm font-bold shadow-md transition-all"
        >
          <MdAdd className="text-lg" />
          Add Slide
        </button>
      </div>

      {successMsg && (
        <div className="bg-emerald-50 text-emerald-700 border border-emerald-200 px-4 py-3 rounded-lg text-sm font-semibold">
          {successMsg}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-600" />
        </div>
      ) : slides.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-slate-500 shadow-sm">
          <MdOutlinePhotoSizeSelectActual className="text-5xl mx-auto text-slate-300 mb-3" />
          <p>No slides found. Click "Add Slide" to upload one.</p>
        </div>
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {slides.map((slide) => {
            const imgUrl = slide.image 
              ? `${axiosInstance.defaults.baseURL}/${slide.image}`
              : null;
            
            return (
              <article key={slide.id} className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden flex flex-col justify-between">
                <div className="relative bg-slate-950 h-44 flex items-center justify-center p-4">
                  {imgUrl ? (
                    <img src={imgUrl} alt={slide.label} className="h-full w-full object-contain filter drop-shadow-lg" />
                  ) : (
                    <span className="text-slate-500 text-sm">No Image</span>
                  )}
                  <div className="absolute top-3 right-3">
                    <button
                      onClick={() => handleToggleActive(slide.id)}
                      className={`inline-flex items-center gap-0.5 px-2.5 py-1 rounded-full text-[10px] uppercase font-bold tracking-wider shadow-sm transition-all border ${
                        parseInt(slide.is_active) === 1
                          ? "bg-emerald-500 border-emerald-500 text-white"
                          : "bg-slate-800 border-slate-700 text-slate-400"
                      }`}
                    >
                      {parseInt(slide.is_active) === 1 ? "Active" : "Inactive"}
                    </button>
                  </div>
                </div>

                <div className="p-4 space-y-3">
                  <div>
                    <h3 className="font-semibold text-slate-900 text-sm uppercase tracking-wider text-slate-400">Overlay Title</h3>
                    <p className="mt-1 text-slate-800 font-bold text-lg">{slide.label}</p>
                  </div>

                  <div className="flex justify-end gap-2 border-t border-slate-100 pt-3">
                    <button
                      onClick={() => handleOpenEdit(slide)}
                      className="flex items-center gap-1 text-slate-650 hover:text-indigo-600 bg-slate-50 hover:bg-slate-100 px-3 py-1.5 rounded-lg text-xs font-semibold transition border border-slate-250"
                    >
                      <MdEdit /> Edit
                    </button>
                    <button
                      onClick={() => handleDelete(slide.id)}
                      className="flex items-center gap-1 text-slate-650 hover:text-rose-600 bg-slate-50 hover:bg-rose-50 px-3 py-1.5 rounded-lg text-xs font-semibold transition border border-slate-250 hover:border-rose-100"
                    >
                      <MdDelete /> Delete
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {/* MODAL */}
      {modalOpen && (
        <div className="fixed inset-0 z-[100] bg-slate-950/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden border border-slate-100 animate-in fade-in zoom-in-95 duration-200">
            <div className="border-b border-slate-100 px-6 py-4 flex justify-between items-center bg-slate-50">
              <h2 className="text-lg font-bold text-slate-950">
                {editingSlide ? "Edit Slide Settings" : "Add Slide to Homepage"}
              </h2>
              <button onClick={() => setModalOpen(false)} className="text-slate-400 hover:text-slate-600 text-xl">
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {error && (
                <div className="bg-rose-50 text-rose-600 border border-rose-100 px-4 py-2.5 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <label className="block space-y-1">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Overlay Label / Name</span>
                <input
                  type="text"
                  required
                  placeholder="e.g. Sport Series, Cruiser Edition"
                  value={form.label}
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-amber-500 focus:bg-white transition-all"
                />
              </label>

              <div className="space-y-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 block">Slide Image</span>
                <div className="flex flex-col items-center gap-4 border border-dashed border-slate-200 rounded-lg p-4 bg-slate-50/50 text-center">
                  {imagePreview ? (
                    <img src={imagePreview} alt="Preview" className="h-32 w-48 object-contain bg-slate-950 border border-slate-800 rounded p-1" />
                  ) : (
                    <div className="h-32 w-full bg-slate-100 flex items-center justify-center text-slate-450 text-xs rounded border border-dashed">
                      No Photo Selected
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    id="slide-image-file"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                  <label
                    htmlFor="slide-image-file"
                    className="inline-block bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 px-4 py-2 rounded-lg text-xs font-bold cursor-pointer transition shadow-sm"
                  >
                    {imagePreview ? "Change Photo" : "Upload Photo"}
                  </label>
                  <p className="text-[10px] text-slate-400">Supports PNG with transparent backgrounds for best visual effect.</p>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="px-5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-semibold transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 bg-amber-500 hover:bg-amber-600 text-slate-950 rounded-lg text-sm font-bold shadow-md transition"
                >
                  {editingSlide ? "Save Changes" : "Add Slide"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomeSlides;
